<template>
  <ai-visibility-sidebar
    :open="open"
    :aria-label="`${competitorName} competitor details`"
    close-label="Close competitor details"
    @close="$emit('close')"
    @after-close="$emit('after-close')"
  >
      <section class="ai-visibility-competitor-sidebar__hero">
        <span class="ai-visibility-competitor-sidebar__initial">
          {{ competitor.initial }}
        </span>
        <div class="ai-visibility-competitor-sidebar__title">
          <h2>{{ competitorName }}</h2>
          <p>{{ subtitle }}</p>
        </div>
      </section>

      <section class="ai-visibility-competitor-sidebar__metrics" aria-label="Competitor summary">
        <div
          v-for="metric in summaryMetrics"
          :key="metric.label"
          class="ai-visibility-competitor-sidebar__metric"
        >
          <span>{{ metric.label }}</span>
          <strong>{{ metric.value }}</strong>
        </div>
      </section>

      <section class="ai-visibility-competitor-sidebar__section">
        <div class="ai-visibility-competitor-sidebar__section-heading">
          <h3>Head-to-head vs. you</h3>
          <p>Across {{ totalQuestionCount }} tracked questions this week</p>
        </div>
        <div class="ai-visibility-competitor-sidebar__head-to-head">
          <article
            v-for="card in headToHeadCards"
            :key="card.label"
            class="ai-visibility-competitor-sidebar__stat-card"
            :class="{ 'ai-visibility-competitor-sidebar__stat-card--alert': card.alert }"
          >
            <strong>{{ card.value }}</strong>
            <span>{{ card.label }}</span>
          </article>
        </div>
      </section>

      <section class="ai-visibility-competitor-sidebar__section">
        <div class="ai-visibility-competitor-sidebar__section-heading">
          <h3>Trend over {{ trendWindowLabel }}</h3>
        </div>
        <div class="ai-visibility-competitor-sidebar__trend-tabs" role="tablist">
          <button
            v-for="tab in trendTabs"
            :key="tab.key"
            type="button"
            role="tab"
            :aria-selected="activeTrendKey === tab.key ? 'true' : 'false'"
            class="ai-visibility-competitor-sidebar__trend-tab"
            :class="{ 'ai-visibility-competitor-sidebar__trend-tab--active': activeTrendKey === tab.key }"
            @click="activeTrendKey = tab.key"
          >
            {{ tab.label }}
          </button>
        </div>
        <div class="ai-visibility-competitor-sidebar__chart">
          <apex-chart
            v-if="hasTrendData"
            type="line"
            height="190"
            :options="chartOptions"
            :series="chartSeries"
          />
          <p v-else class="ai-visibility-competitor-sidebar__empty">
            No trend data for this competitor yet.
          </p>
        </div>
      </section>

      <section class="ai-visibility-competitor-sidebar__section">
        <div class="ai-visibility-competitor-sidebar__section-heading">
          <h3>Questions where AI mentioned this competitor</h3>
          <p>{{ mentionedQuestions.length }} total questions across these answers</p>
        </div>
        <div v-if="mentionedQuestions.length" class="ai-visibility-competitor-sidebar__question-list">
          <div
            v-for="question in mentionedQuestions"
            :key="question.questionId"
            class="ai-visibility-competitor-sidebar__question-row"
          >
            <span>{{ question.questionText }}</span>
            <status-pill
              :label="formatRankLabel('Them', question.theirRank)"
              tone="danger"
            />
            <status-pill
              :label="formatRankLabel('You', question.yourRank)"
              :tone="question.yourRank == null ? 'neutral' : 'success'"
            />
          </div>
        </div>
        <p v-else class="ai-visibility-competitor-sidebar__empty">
          No mentioned questions for this competitor yet.
        </p>
      </section>

      <section class="ai-visibility-competitor-sidebar__section">
        <div class="ai-visibility-competitor-sidebar__section-heading">
          <h3>Sources AI reads when mentioning them</h3>
          <p>Top domains cited in answers that include {{ competitorName }}</p>
        </div>
        <div v-if="sources.length" class="ai-visibility-competitor-sidebar__source-list">
          <div
            v-for="source in sources"
            :key="source.domain"
            class="ai-visibility-competitor-sidebar__source-row"
          >
            <div class="ai-visibility-competitor-sidebar__source-name">
              <source-avatar :domain="source.domain" />
              <span class="domain-name">{{ source.domain }}</span>
              <panel-badge v-if="source.domain === trackedDomain">You</panel-badge>
            </div>
            <span>{{ source.citationCount }}x</span>
          </div>
        </div>
        <p v-else class="ai-visibility-competitor-sidebar__empty">
          No cited sources for this competitor yet.
        </p>
      </section>

      <button
        type="button"
        class="ai-visibility-competitor-sidebar__citations-link"
        @click="$emit('view-citations')"
      >
        View in Citations
        <i class="material-icons">arrow_forward</i>
      </button>
  </ai-visibility-sidebar>
</template>

<script>
import AiVisibilitySidebar from '../../../ui/overlays/AiVisibilitySidebar.vue'
import PanelBadge from '../../../ui/display/PanelBadge.vue'
import SourceAvatar from '../../../ui/display/SourceAvatar.vue'
import StatusPill from '../../../ui/display/StatusPill.vue'

const TREND_TABS = [
  {
    key: 'mentionRate',
    label: 'Mention rate',
    competitorHistoryKey: 'mentionRateHistory',
    yourHistoryKey: 'yourMentionRateHistory',
    suffix: '%',
  },
  {
    key: 'averageRank',
    label: 'Avg. rank',
    competitorHistoryKey: 'averageRankHistory',
    yourHistoryKey: 'yourAverageRankHistory',
    prefix: '#',
    reversed: true,
  },
]

export default {
  name: 'CompetitorDetailsSidebar',
  components: {
    AiVisibilitySidebar,
    PanelBadge,
    SourceAvatar,
    StatusPill,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    competitor: {
      type: Object,
      required: true,
    },
    metrics: {
      type: Object,
      default() {
        return {}
      },
    },
    trackedDomain: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      activeTrendKey: 'mentionRate',
    }
  },
  computed: {
    competitorName() {
      return this.competitor.brandName || this.competitor.domain || 'Competitor'
    },
    subtitle() {
      if (this.competitor.isTrackedDomain) {
        return 'Your site'
      }

      return `Competitor`
    },
    totalQuestionCount() {
      return Number(this.metrics.totalQuestionCount || 0)
    },
    summaryMetrics() {
      return [
        {
          label: 'Mention rate',
          value: `${Number(this.competitor.mentionRatePercent || 0)}% of answers`,
        },
        {
          label: 'Avg. rank',
          value: `${this.competitor.averagePositionLabel || '-'}`,
        },
      ]
    },
    headToHead() {
      return this.competitor.detail?.headToHead || {}
    },
    headToHeadCards() {
      return [
        {
          value: Number(this.headToHead.sameQuestionCount || 0),
          label: 'Same questions - both of you appear',
        },
        {
          value: Number(this.headToHead.theyAppearYouDoNotQuestionCount || 0),
          label: "They appear, you don't - your biggest opening",
          alert: true,
        },
        {
          value: Number(this.headToHead.bothAppearTheyRankHigherQuestionCount || 0),
          label: 'Both appear, they rank higher',
        },
      ]
    },
    trendTabs() {
      return TREND_TABS
    },
    activeTrend() {
      return this.trendTabs.find(tab => tab.key === this.activeTrendKey) || this.trendTabs[0]
    },
    competitorHistory() {
      return this.competitor[this.activeTrend.competitorHistoryKey] || []
    },
    yourHistory() {
      return this.metrics[this.activeTrend.yourHistoryKey] || []
    },
    trendCategories() {
      const dates = [...this.competitorHistory, ...this.yourHistory].map(point => point.x).filter(Boolean)
      return Array.from(new Set(dates)).sort()
    },
    formattedTrendCategories() {
      return this.trendCategories.map(value => this.formatChartDate(value))
    },
    chartSeries() {
      return [
        {
          name: this.competitorName,
          data: this.buildSeriesValues(this.competitorHistory),
        },
        {
          name: 'You',
          data: this.buildSeriesValues(this.yourHistory),
        },
      ]
    },
    hasTrendData() {
      return this.chartSeries.some(series => series.data.some(value => value !== null && value !== undefined))
    },
    chartOptions() {
      return {
        chart: {
          toolbar: {show: false},
          zoom: {enabled: false},
        },
        colors: ['var(--v-profileAnchorColor-base)', 'var(--v-link3Color-base)'],
        dataLabels: {enabled: false},
        grid: {
          borderColor: 'var(--v-gray13Color-base)',
          strokeDashArray: 4,
          padding: {
            left: 8,
            right: 8,
          },
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          labels: {
            colors: 'var(--v-mainColor-base)',
          },
        },
        markers: {
          size: 4,
          strokeWidth: 2,
          strokeColors: 'var(--v-cardBg-base)',
        },
        stroke: {
          curve: 'straight',
          width: 2,
        },
        tooltip: {
          y: {
            formatter: value => this.formatChartValue(value),
          },
        },
        xaxis: {
          categories: this.formattedTrendCategories,
          labels: {
            style: {
              colors: 'var(--v-mainColor-base)',
              fontSize: '12px',
            },
          },
          axisBorder: {show: false},
          axisTicks: {show: false},
        },
        yaxis: {
          reversed: Boolean(this.activeTrend.reversed),
          labels: {
            formatter: value => this.formatChartValue(value),
            style: {
              colors: 'var(--v-mainColor-base)',
              fontSize: '12px',
            },
          },
        },
      }
    },
    trendWindowLabel() {
      const count = this.trendCategories.length
      return count > 1 ? `${count} runs` : 'latest run'
    },
    mentionedQuestions() {
      return this.competitor.detail?.mentionedQuestions || []
    },
    sources() {
      return this.competitor.detail?.sources || []
    },
  },
  methods: {
    buildSeriesValues(history) {
      const valuesByDate = history.reduce((values, point) => {
        values[point.x] = point.y
        return values
      }, {})

      return this.trendCategories.map(date => {
        const value = valuesByDate[date]
        return value === undefined ? null : value
      })
    },
    formatChartDate(value) {
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) {
        return String(value)
      }

      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
      }).format(parsed)
    },
    formatChartValue(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return ''
      }

      const formattedValue = Number(value) % 1 === 0
        ? String(Number(value))
        : Number(value).toFixed(1)

      return `${this.activeTrend.prefix || ''}${formattedValue}${this.activeTrend.suffix || ''}`
    },
    formatRankLabel(prefix, rank) {
      if (rank === null || rank === undefined) {
        return `${prefix} missing`
      }

      return `${prefix} #${Math.round(Number(rank))}`
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-competitor-sidebar__citations-link:focus {
  outline: 2px solid var(--v-lightYellowColor-base);
  outline-offset: 2px;
}

.ai-visibility-competitor-sidebar__hero {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.ai-visibility-competitor-sidebar__initial {
  display: inline-flex;
  width: 45px;
  height: 45px;
  flex: 0 0 45px;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background: var(--v-profileAnchorColor-base);
  color: var(--v-whiteColor-base);
  font-size: 20px;
  font-weight: 800;
}

.ai-visibility-competitor-sidebar__title {
  min-width: 0;
}

.ai-visibility-competitor-sidebar__title h2,
.ai-visibility-competitor-sidebar__section-heading h3 {
  color: var(--v-mainColor-base);
  font-weight: 700;
  line-height: 1.2;
  margin: 0;
}

.ai-visibility-competitor-sidebar__title h2 {
  overflow: hidden;
  font-size: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-visibility-competitor-sidebar__section-heading p {
  color: #363636 !important;
}

.ai-visibility-competitor-sidebar__title p,
.ai-visibility-competitor-sidebar__section-heading p {
  color: var(--v-darkGrayColor-base);
  font-size: 16px;
  line-height: 1.5;
  margin: 4px 0 0;
}

.ai-visibility-competitor-sidebar__metrics {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.ai-visibility-competitor-sidebar__metric {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
}

.ai-visibility-competitor-sidebar__metric span {
  color: #363636;
  font-size: 16px;
  line-height: 1.5;
}

.ai-visibility-competitor-sidebar__metric strong {
  color: var(--v-mainColor-base);
  font-size: 16px;
  font-weight: 700;
  line-height: 1.25;
}

.ai-visibility-competitor-sidebar__stat-card,
.ai-visibility-competitor-sidebar__chart,
.ai-visibility-competitor-sidebar__question-list,
.ai-visibility-competitor-sidebar__source-list {
  border: 1px solid var(--v-gray13Color-base);
  border-radius: 8px;
  background: var(--v-cardBg-base);
  box-shadow: 0 5px 2px 0 rgba(0, 0, 0, 0.01), 0 3px 2px 0 rgba(0, 0, 0, 0.04), 0 1px 1px 0 rgba(0, 0, 0, 0.08), 0 0 1px 0 rgba(0, 0, 0, 0.08);
}

.ai-visibility-competitor-sidebar__section-heading h3 {
  font-size: 16px;
}

.ai-visibility-competitor-sidebar__section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ai-visibility-competitor-sidebar__head-to-head {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.ai-visibility-competitor-sidebar__stat-card {
  min-height: 124px;
  padding: 18px;
}

.ai-visibility-competitor-sidebar__stat-card--alert {
  border-color: var(--v-lightYellowColor-base);
  background: linear-gradient(69deg, #FFF 65.79%, #FFF3ED 98.62%);
  box-shadow: 0 5px 2px 0 rgba(0, 0, 0, 0.01), 0 3px 2px 0 rgba(0, 0, 0, 0.04), 0 1px 1px 0 rgba(0, 0, 0, 0.08), 0 0 1px 0 rgba(0, 0, 0, 0.08);
}

.ai-visibility-competitor-sidebar__stat-card strong {
  display: block;
  color: var(--v-mainColor-base);
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  margin-bottom: 10px;
}

.ai-visibility-competitor-sidebar__stat-card--alert strong {
  color: var(--v-red11Color-base);
}

.ai-visibility-competitor-sidebar__stat-card span {
  color: var(--v-blackColor-base);
  font-size: 14px;
  line-height: 1.4;
}

.ai-visibility-competitor-sidebar__trend-tabs {
  display: flex;
  overflow-x: auto;
  border-bottom: 1px solid var(--v-gray13Color-base);
  background: var(--v-cardBg-base);
  gap: 12px;
}

.ai-visibility-competitor-sidebar__trend-tab {
  flex: 0 0 auto;
  border: 0;
  border-bottom: 4px solid transparent;
  background: transparent;
  color: var(--v-blackColor-base);
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  padding: 10px;
}

.ai-visibility-competitor-sidebar__trend-tab--active {
  border-bottom-color: var(--v-profileAnchorColor-base);
  background: var(--v-lightYellowColor-base);
  color: var(--v-profileAnchorColor-base);
  font-weight: 700;
  border-radius: 5px 5px 0 0;
}

.ai-visibility-competitor-sidebar__chart {
  padding: 16px 12px 6px;
}

.ai-visibility-competitor-sidebar__question-list,
.ai-visibility-competitor-sidebar__source-list {
  overflow: hidden;
}

.ai-visibility-competitor-sidebar__question-row,
.ai-visibility-competitor-sidebar__source-row {
  display: grid;
  align-items: center;
  gap: 12px;
  border-bottom: 1px dashed var(--v-gray13Color-base);
  padding: 10px 12px;
  grid-auto-flow: column;
  grid-auto-columns: 4fr 1fr 1fr;
}

.ai-visibility-competitor-sidebar__source-row {
  display: flex;
  justify-content: space-between;
}

.ai-visibility-competitor-sidebar__question-row:last-child,
.ai-visibility-competitor-sidebar__source-row:last-child {
  border-bottom: 0;
}

.ai-visibility-competitor-sidebar__question-row > span:first-child {
  flex: 1 1 auto;
  min-width: 0;
  color: var(--v-mainColor-base);
  font-size: 14px;
  line-height: 1.4;
}

.ai-visibility-competitor-sidebar__question-row ::v-deep .ai-visibility-status-pill {
  flex: 0 0 auto;
}

.ai-visibility-competitor-sidebar__source-name {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.ai-visibility-competitor-sidebar__source-name > .domain-name {
  overflow: hidden;
  color: var(--v-mainColor-base);
  font-size: 16px;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-visibility-competitor-sidebar__source-row > span {
  color: var(--v-blackColor-base);
  font-size: 16px;
  line-height: 1.5;
  padding: 10px;
}

.ai-visibility-competitor-sidebar__empty {
  color: var(--v-darkGrayColor-base);
  font-size: 14px;
  line-height: 1.4;
  margin: 0;
}

.ai-visibility-competitor-sidebar__citations-link {
  display: inline-flex;
  align-self: flex-end;
  align-items: center;
  gap: 4px;
  border: 0;
  background: transparent;
  color: var(--v-profileAnchorColor-base);
  cursor: pointer;
  font-size: 16px;
  line-height: 1.5;
  padding: 0;
}

.ai-visibility-competitor-sidebar__citations-link i {
  font-size: 18px;
}

@media (max-width: 680px) {
  .ai-visibility-competitor-sidebar__metrics,
  .ai-visibility-competitor-sidebar__head-to-head {
    grid-template-columns: 1fr;
  }

  .ai-visibility-competitor-sidebar__question-row {
    align-items: flex-start;
    flex-wrap: wrap;
  }
}
</style>
